import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import { SERVICES as STATIC_SERVICES, U } from "@/data/services";
import { listServices, ServiceRow } from "@/lib/api";

const HERO_BG = U("1505373877841-8d25f7d46678", 2400, 88);

// Reference-image crops shared with the homepage grid. Keyed by service slug.
const SERVICE_IMAGES: Record<string, string> = {
  "audio":              "/services/audio.jpg",
  "lighting":           "/services/lighting.jpg",
  "led-video":          "/services/led-video.jpg",
  "projection-mapping": "/services/projection-mapping.jpg",
  "rigging-staging":    "/services/rigging-staging.jpg",
  "sfx-laser":          "/services/sfx-laser.jpg",
  "technical-drawings": "/services/technical-drawings.jpg",
  "3d-visualisation":   "/services/3d-visualisation.jpg",
};
const AV_SIM_IMG = "/services/av-simulation-studio.jpg";

// SVG icon set matching the homepage services grid.
const svgP = { viewBox: "0 0 24 24", width: 24, height: 24, fill: "none", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
const SVC_ICONS: Record<string, React.ReactNode> = {
  "audio":              <svg {...svgP}><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>,
  "lighting":           <svg {...svgP}><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/></svg>,
  "led-video":          <svg {...svgP}><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
  "projection-mapping": <svg {...svgP}><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>,
  "rigging-staging":    <svg {...svgP}><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
  "sfx-laser":          <svg {...svgP}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  "technical-drawings": <svg {...svgP}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  "3d-visualisation":   <svg {...svgP}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>,
};
const FALLBACK_ICON = <svg {...svgP}><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/></svg>;
// 10th card icon — monitor with 3D cube inside
const SIM_ICON = (
  <svg {...svgP}>
    <rect x="2" y="3" width="20" height="14" rx="2"/>
    <line x1="8" y1="21" x2="16" y2="21"/>
    <line x1="12" y1="17" x2="12" y2="21"/>
    <polygon points="9 7 15 7 15 13 9 13 9 7" />
    <line x1="9" y1="7" x2="12" y2="5"/>
    <line x1="15" y1="7" x2="12" y2="5"/>
    <line x1="15" y1="13" x2="12" y2="15"/>
    <line x1="9" y1="13" x2="12" y2="15"/>
    <line x1="12" y1="5" x2="12" y2="15"/>
  </svg>
);

interface CardData { slug: string; name: string; short: string }

const Services: React.FC = () => {
  useReveal();
  const [items, setItems] = useState<CardData[]>(STATIC_SERVICES.map(s => ({ slug: s.slug, name: s.name, short: s.shortDesc })));

  useEffect(() => {
    listServices(true).then((rows: ServiceRow[]) => {
      if (rows.length === 0) return;
      setItems(rows.map(r => ({ slug: r.slug, name: r.name, short: r.short_description || "" })));
    }).catch(() => { /* keep static fallback */ });
  }, []);

  return (
    <main className="iav-page" data-testid="services-page">
      <section className="iav-page-hero iav-hero-bg" style={{ backgroundImage: `url(${HERO_BG})` }}>
        <div className="iav-page-hero-overlay" />
        <div className="iav-page-hero-inner">
          <span className="iav-tag-line" data-reveal>What We Do</span>
          <h1 data-reveal>OUR<br /><span className="iav-accent">SERVICES</span></h1>
          <p className="lead" data-reveal>End-to-end technical production. Nine specialist departments. One coordinated delivery.</p>
        </div>
      </section>
      <section className="iav-section">
        <div className="iav-svc-grid" data-testid="services-grid">
          {items.map((s, i) => {
            const img = SERVICE_IMAGES[s.slug];
            if (img) {
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="iav-svc-card iav-svc-card-image"
                  data-reveal
                  data-cursor="link"
                  data-testid={`service-card-${s.slug}`}
                  aria-label={`${s.name} — ${s.short}`}
                >
                  <div className="iav-svc-bg" style={{ backgroundImage: `url(${img})` }} aria-hidden />
                  <span className="iav-svc-sr">{s.name}. {s.short}</span>
                </Link>
              );
            }
            // Fallback for admin-added services without a reference image
            return (
              <Link key={s.slug} to={`/services/${s.slug}`} className="iav-svc-card" data-reveal data-cursor="link" data-testid={`service-card-${s.slug}`}>
                <div className="iav-svc-num">{String(i + 1).padStart(2, "0")}</div>
                <div className="iav-svc-icon-wrap" aria-hidden>
                  <span className="iav-svc-svg">{SVC_ICONS[s.slug] || FALLBACK_ICON}</span>
                </div>
                <div className="iav-svc-name">{s.name}</div>
                <div className="iav-svc-desc">{s.short}</div>
                <div className="iav-svc-arrow">→</div>
              </Link>
            );
          })}
          {/* 10th card — AV Simulation Studio (external app) */}
          <a
            href="https://www.avsimulationstudio.com"
            target="_blank"
            rel="noopener noreferrer"
            className="iav-svc-card iav-svc-card-image iav-svc-card-featured"
            data-reveal
            data-cursor="link"
            data-testid="service-card-av-simulation-studio"
            aria-label="AV Simulation Studio — 3D pre-visualisation for projection mapping, LED walls, truss and rigging"
          >
            <div className="iav-svc-bg" style={{ backgroundImage: `url(${AV_SIM_IMG})` }} aria-hidden />
            <span className="iav-svc-sr">AV Simulation Studio. 3D pre-visualisation for projection mapping, LED walls, truss and rigging — design your show before load-in. External link.</span>
          </a>
        </div>
      </section>
    </main>
  );
};
export default Services;
